/**
 * StandardsLibrary Component
 * CAD standards, naming conventions, layer specs, and templates
 */

import React, { useState } from 'react';

const StandardsLibrary: React.FC = () => {
  const [activeSection, setActiveSection] = useState('naming');
  const [searchQuery, setSearchQuery] = useState('');

  // File naming convention
  const namingConvention = {
    format: '{PROJECT_ID}-{DRAWING_TYPE}-{SHEET_NUM}-{REV}',
    example: 'PRJ-2024-0042-SD-A1-R0.dwg',
    types: [
      { code: 'SD', name: 'Shop Drawing', description: 'Main shop drawings showing assembly details' },
      { code: 'DT', name: 'Detail', description: 'Enlarged detail views' },
      { code: 'TP', name: 'Taper Plan', description: 'Roof slope and drainage plans' },
      { code: 'SC', name: 'Section', description: 'Cross-section views' },
      { code: 'PL', name: 'Plan', description: 'Plan views' },
      { code: 'EL', name: 'Elevation', description: 'Elevation views' },
      { code: 'SK', name: 'Sketch', description: 'Preliminary sketches' },
      { code: 'AS', name: 'As-Built', description: 'Final as-built documentation' },
    ]
  };

  // Layer standards
  const layerStandards = [
    { name: 'ROOF-MEMBRANE', color: 'Cyan', lineweight: 0.30, description: 'Membrane outline and seams' },
    { name: 'ROOF-INSULATION', color: 'Yellow', lineweight: 0.25, description: 'Insulation boundaries and tapers' },
    { name: 'ROOF-DECK', color: 'White', lineweight: 0.35, description: 'Deck structure and openings' },
    { name: 'ROOF-DRAIN', color: 'Blue', lineweight: 0.30, description: 'Drains, scuppers, overflow' },
    { name: 'ROOF-EDGE', color: 'Red', lineweight: 0.40, description: 'Edge metal and terminations' },
    { name: 'ROOF-PENETRATION', color: 'Magenta', lineweight: 0.30, description: 'Pipes, vents, curbs' },
    { name: 'ROOF-DIM', color: 'Green', lineweight: 0.18, description: 'Dimensions and annotations' },
    { name: 'ROOF-TEXT', color: 'White', lineweight: 0.18, description: 'Labels and notes' },
    { name: 'ROOF-HATCH', color: 'Gray', lineweight: 0.13, description: 'Area hatches and fills' },
    { name: 'ROOF-GRID', color: 'Gray', lineweight: 0.09, description: 'Reference grid' },
    { name: 'ROOF-XREF', color: 'Gray', lineweight: 0.13, description: 'External references' },
    { name: 'DEFPOINTS', color: 'White', lineweight: 0.00, description: 'Definition points (non-plot)' },
  ];

  // Templates
  const templates = [
    { id: 'roof-plan', name: 'Roof Plan Template', size: '24x36', format: 'DWG', description: 'Standard roof plan with title block' },
    { id: 'detail-sheet', name: 'Detail Sheet Template', size: '24x36', format: 'DWG', description: 'Multi-detail sheet layout' },
    { id: 'section-template', name: 'Section Template', size: '24x36', format: 'DWG', description: 'Standard section views' },
    { id: 'title-block', name: 'Title Block', size: 'Various', format: 'DWG', description: 'Company title block with fields' },
    { id: 'detail-library', name: 'Detail Library', size: 'Various', format: 'DWG', description: 'Common roof details' },
    { id: 'symbol-library', name: 'Symbol Library', size: 'Various', format: 'DWG', description: 'Standard symbols and leaders' },
  ];

  // Drafting standards
  const draftingStandards = [
    {
      category: 'Dimensions',
      rules: [
        'Use architectural units (feet-inches)',
        'Primary dimensions in feet, secondary in inches',
        'Dimension text height: 3/32"',
        'Arrow size: 1/8"',
        'Extension line offset: 1/16"',
      ]
    },
    {
      category: 'Text',
      rules: [
        'Title text: 1/4" height, BOLD',
        'Subtitle text: 3/16" height',
        'General notes: 3/32" height',
        'Font: Romans.shx or Arial',
        'All caps for titles, mixed case for notes',
      ]
    },
    {
      category: 'Hatches',
      rules: [
        'Membrane: ANSI31 @ 1/4" scale',
        'Insulation: INSUL @ 1/8" scale',
        'Concrete: AR-CONC @ 1/4" scale',
        'Metal deck: STEEL @ 1/8" scale',
        'Wood: WOOD @ 1/4" scale',
      ]
    },
    {
      category: 'Scale',
      rules: [
        'Plan views: 1/8" = 1\'-0"',
        'Details: 1-1/2" = 1\'-0" or 3" = 1\'-0"',
        'Sections: 3/4" = 1\'-0"',
        'Site plans: 1" = 20\'-0" or smaller',
        'Always note scale on drawing',
      ]
    },
  ];

  // Local search filtering across all sections
  const query = searchQuery.trim().toLowerCase();
  const matchesQuery = (...fields: string[]) =>
    !query || fields.some(field => field.toLowerCase().includes(query));

  const filteredTypes = namingConvention.types.filter(type =>
    matchesQuery(type.code, type.name, type.description)
  );
  const filteredLayers = layerStandards.filter(layer =>
    matchesQuery(layer.name, layer.color, layer.description)
  );
  const filteredTemplates = templates.filter(template =>
    matchesQuery(template.name, template.description, template.format, template.size)
  );
  const filteredDrafting = draftingStandards.filter(standard =>
    matchesQuery(standard.category, ...standard.rules)
  );

  return (
    <div className="standards-library">
      {/* Header */}
      <div className="library-header">
        <h2>📐 Standards Library</h2>
        <div className="header-actions">
          <div className="search-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search standards..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="section-nav">
        <button 
          className={`nav-btn ${activeSection === 'naming' ? 'active' : ''}`}
          onClick={() => setActiveSection('naming')}
        >
          📝 File Naming
        </button>
        <button 
          className={`nav-btn ${activeSection === 'layers' ? 'active' : ''}`}
          onClick={() => setActiveSection('layers')}
        >
          🎨 Layer Standards
        </button>
        <button 
          className={`nav-btn ${activeSection === 'drafting' ? 'active' : ''}`}
          onClick={() => setActiveSection('drafting')}
        >
          📏 Drafting Standards
        </button>
        <button 
          className={`nav-btn ${activeSection === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveSection('templates')}
        >
          📄 Templates
        </button>
      </div>

      {/* Content */}
      <div className="library-content">
        {/* File Naming Section */}
        {activeSection === 'naming' && (
          <div className="naming-section">
            <div className="format-card">
              <h3>File Naming Format</h3>
              <div className="format-display">
                <code>{namingConvention.format}</code>
              </div>
              <div className="example">
                <span className="label">Example:</span>
                <code>{namingConvention.example}</code>
              </div>
              <div className="format-breakdown">
                <div className="breakdown-item">
                  <span className="part">PROJECT_ID</span>
                  <span className="desc">Project number (e.g., PRJ-2024-0042)</span>
                </div>
                <div className="breakdown-item">
                  <span className="part">DRAWING_TYPE</span>
                  <span className="desc">2-letter type code (see below)</span>
                </div>
                <div className="breakdown-item">
                  <span className="part">SHEET_NUM</span>
                  <span className="desc">Sheet identifier (A1, A2, D1, etc.)</span>
                </div>
                <div className="breakdown-item">
                  <span className="part">REV</span>
                  <span className="desc">Revision number (R0, R1, R2...)</span>
                </div>
              </div>
            </div>

            <div className="types-card">
              <h3>Drawing Type Codes</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Type</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTypes.map(type => (
                    <tr key={type.code}>
                      <td><code className="type-code">{type.code}</code></td>
                      <td>{type.name}</td>
                      <td className="desc">{type.description}</td>
                    </tr>
                  ))}
                  {filteredTypes.length === 0 && (
                    <tr>
                      <td colSpan={3} className="desc">No type codes match "{searchQuery}"</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Layer Standards Section */}
        {activeSection === 'layers' && (
          <div className="layers-section">
            <div className="layers-intro">
              <p>All drawings must use the following layer naming convention and color standards.</p>
            </div>
            <table className="data-table layers-table">
              <thead>
                <tr>
                  <th>Layer Name</th>
                  <th>Color</th>
                  <th>Lineweight</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {filteredLayers.map(layer => (
                  <tr key={layer.name}>
                    <td><code>{layer.name}</code></td>
                    <td>
                      <span className="color-chip" data-color={layer.color.toLowerCase()}>
                        {layer.color}
                      </span>
                    </td>
                    <td>{layer.lineweight} mm</td>
                    <td className="desc">{layer.description}</td>
                  </tr>
                ))}
                {filteredLayers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="desc">No layers match "{searchQuery}"</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="download-section">
              <button className="btn primary" disabled title="Coming soon">
                📥 Download Layer Template (.dwg)
              </button>
              <button className="btn secondary" disabled title="Coming soon">
                📥 Download Layer Standards (.ctb)
              </button>
            </div>
          </div>
        )}

        {/* Drafting Standards Section */}
        {activeSection === 'drafting' && (
          <div className="drafting-section">
            <div className="standards-grid">
              {filteredDrafting.map(standard => (
                <div key={standard.category} className="standard-card">
                  <h4>{standard.category}</h4>
                  <ul>
                    {standard.rules.map((rule, i) => (
                      <li key={i}>{rule}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {filteredDrafting.length === 0 && (
              <p className="desc">No drafting standards match "{searchQuery}"</p>
            )}
          </div>
        )}

        {/* Templates Section */}
        {activeSection === 'templates' && (
          <div className="templates-section">
            <div className="templates-grid">
              {filteredTemplates.map(template => (
                <div key={template.id} className="template-card">
                  <div className="template-icon">📄</div>
                  <div className="template-info">
                    <h4>{template.name}</h4>
                    <p>{template.description}</p>
                    <div className="template-meta">
                      <span>{template.size}</span>
                      <span>•</span>
                      <span>{template.format}</span>
                    </div>
                  </div>
                  <button className="btn small" disabled title="Coming soon">Download</button>
                </div>
              ))}
            </div>
            {filteredTemplates.length === 0 && (
              <p className="desc">No templates match "{searchQuery}"</p>
            )}
          </div>
        )}
      </div>

      <style>{`
        .standards-library {
          padding: 0;
        }

        .library-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-tertiary, #334155);
          padding: 8px 16px;
          border-radius: 8px;
          width: 300px;
        }

        .search-box input {
          background: none;
          border: none;
          color: var(--text-primary);
          flex: 1;
          outline: none;
        }

        .section-nav {
          display: flex;
          gap: 8px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .nav-btn {
          padding: 10px 20px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s;
        }

        .nav-btn:hover {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary);
        }

        .nav-btn.active {
          background: var(--primary);
          border-color: var(--primary);
          color: white;
        }

        .format-card, .types-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .format-card h3, .types-card h3 {
          margin: 0 0 16px;
        }

        .format-display {
          background: var(--bg-primary);
          padding: 16px 24px;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .format-display code {
          font-size: 18px;
          color: var(--accent);
        }

        .example {
          margin-bottom: 24px;
        }

        .example .label {
          color: var(--text-secondary);
          margin-right: 8px;
        }

        .example code {
          color: var(--primary);
        }

        .format-breakdown {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .breakdown-item {
          display: flex;
          gap: 16px;
          padding: 12px;
          background: var(--bg-primary);
          border-radius: 6px;
        }

        .breakdown-item .part {
          font-family: monospace;
          color: var(--accent);
          width: 140px;
          flex-shrink: 0;
        }

        .breakdown-item .desc {
          color: var(--text-secondary);
        }

        .type-code {
          background: var(--primary);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 600;
        }

        .layers-intro {
          margin-bottom: 16px;
          color: var(--text-secondary);
        }

        .layers-table code {
          background: var(--bg-primary);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .color-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 13px;
        }

        .color-chip::before {
          content: '';
          width: 12px;
          height: 12px;
          border-radius: 50%;
        }

        .color-chip[data-color="cyan"]::before { background: #00ffff; }
        .color-chip[data-color="yellow"]::before { background: #ffff00; }
        .color-chip[data-color="white"]::before { background: #ffffff; }
        .color-chip[data-color="blue"]::before { background: #0000ff; }
        .color-chip[data-color="red"]::before { background: #ff0000; }
        .color-chip[data-color="magenta"]::before { background: #ff00ff; }
        .color-chip[data-color="green"]::before { background: #00ff00; }
        .color-chip[data-color="gray"]::before { background: #808080; }

        .download-section {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .standards-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .standard-card {
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 20px;
        }

        .standard-card h4 {
          margin: 0 0 16px;
          color: var(--primary);
        }

        .standard-card ul {
          margin: 0;
          padding-left: 20px;
        }

        .standard-card li {
          margin-bottom: 8px;
          color: var(--text-secondary);
        }

        .templates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }

        .template-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color, #334155);
          border-radius: 12px;
          padding: 16px;
        }

        .template-icon {
          font-size: 32px;
        }

        .template-info {
          flex: 1;
        }

        .template-info h4 {
          margin: 0 0 4px;
        }

        .template-info p {
          margin: 0 0 8px;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .template-meta {
          display: flex;
          gap: 8px;
          font-size: 12px;
          color: var(--text-muted);
        }

        .desc {
          color: var(--text-secondary);
          font-size: 13px;
        }

        @media (max-width: 768px) {
          .standards-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default StandardsLibrary;
